
import React, { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Loader2, Share2, MapPin, Star, ExternalLink, Pencil, Check, X, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLeadFilters } from '@/hooks/useLeadFilters';
import SharedLeadsToolbar from '@/components/shared-leads/SharedLeadsToolbar';
import SharedLeadsTable from '@/components/shared-leads/SharedLeadsTable';
import MailMergeDialog from '@/components/leads/mailmerge/MailMergeDialog';
import { toast } from '@/hooks/use-toast';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog';
import { downloadCSV } from '@/services/csv/downloadService';
import { getSmartEmail } from '@/utils/smartEmailUtils';
import { logActivitiesBatch, logActivity } from '@/services/leads/activityService';
import { useCompanyMembers } from '@/hooks/useCompanyMembers';

const SharedWithMe: React.FC = () => {
  const { profile } = useAuth();
  const { data: companyMembers = [] } = useCompanyMembers();
  const { shareId: filterShareId } = useParams<{ shareId?: string }>();
  const companyId = profile?.company_id;
  const userId = profile?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [mailMergeOpen, setMailMergeOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const { data: shares, isLoading } = useQuery({
    queryKey: ['shared-with-me', companyId],
    queryFn: async () => {
      const { data: shareRows, error } = await supabase
        .from('lead_shares')
        .select('*')
        .eq('company_id', companyId!)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const enriched = await Promise.all(
        (shareRows || []).map(async (share) => {
          const { data: leads } = await supabase
            .from('leads')
            .select('id, name, category, address, website, phone, email, rating, image_url')
            .in('id', share.lead_ids);

          const { data: creator } = await supabase
            .from('profiles')
            .select('first_name, last_name, email')
            .eq('id', share.created_by)
            .maybeSingle();

          // Fetch key contacts for smart email selection
          const { data: contacts } = await supabase
            .from('lead_key_contacts')
            .select('id, name, email, phone, position, is_primary, lead_id, status')
            .in('lead_id', share.lead_ids);

          const contactsMap = new Map<string, any[]>();
          (contacts || []).forEach(c => {
            const arr = contactsMap.get(c.lead_id) || [];
            arr.push(c);
            contactsMap.set(c.lead_id, arr);
          });

          return { ...share, leads: leads || [], creator, contactsMap };
        })
      );

      return enriched;
    },
    enabled: !!companyId,
  });

  const flatLeads = useMemo(() => {
    if (!shares) return [];
    const filtered = filterShareId
      ? shares.filter((s: any) => s.id === filterShareId)
      : shares;
    return filtered.flatMap((share) =>
      share.leads.map((lead: any) => ({
        ...lead,
        _shareName: (share as any).name || 'Untitled Share',
        _sharedBy: share.creator
          ? [share.creator.first_name, share.creator.last_name].filter(Boolean).join(' ') || share.creator.email
          : 'Unknown',
        _sharedAt: share.created_at,
        _shareId: share.id,
        _createdBy: share.created_by,
      }))
    );
  }, [shares, filterShareId]);

  const keyContactsMap = useMemo(() => {
    const map = new Map<string, any[]>();
    if (!shares) return map;
    shares.forEach((share: any) => {
      if (share.contactsMap) {
        share.contactsMap.forEach((contacts: any[], leadId: string) => {
          if (!map.has(leadId)) map.set(leadId, contacts);
        });
      }
    });
    return map;
  }, [shares]);

  const filters = useLeadFilters(flatLeads);

  const groupedLeads = useMemo(() => {
    const groups: Record<string, { shareName: string; sharedBy: string; sharedAt: string; leads: any[] }> = {};
    filters.filteredLeads.forEach((lead: any) => {
      const key = `${lead._shareId}`;
      if (!groups[key]) {
        groups[key] = {
          shareName: lead._shareName,
          sharedBy: lead._sharedBy,
          sharedAt: lead._sharedAt,
          leads: [],
        };
      }
      groups[key].leads.push(lead);
    });
    return Object.values(groups);
  }, [filters.filteredLeads]);

  const selectedLeadsForMerge = useMemo(
    () => flatLeads.filter((l) => selectedLeadIds.has(l.id)),
    [flatLeads, selectedLeadIds]
  );

  const handleMailMergeOpen = () => {
    if (selectedLeadIds.size > 0) setMailMergeOpen(true);
  };

  const handleMailMergeClose = (open: boolean) => {
    setMailMergeOpen(open);
    if (!open) setSelectedLeadIds(new Set());
  };

  const handleExportCSV = () => {
    if (selectedLeadsForMerge.length === 0) return;
    const escapeCSV = (val: string) => {
      if (!val) return '';
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };
    const headers = ['Name', 'Category', 'Address', 'Phone', 'Email', 'Website', 'Rating', 'Share', 'Shared By'];
    const rows = selectedLeadsForMerge.map((lead: any) => {
      const smartResult = getSmartEmail(lead, keyContactsMap.get(lead.id));
      return [
        lead.name || '',
        lead.category || '',
        lead.address || '',
        lead.phone || '',
        smartResult?.email || '',
        lead.website || '',
        lead.rating?.toString() || '',
        lead._shareName || '',
        lead._sharedBy || '',
      ].map(escapeCSV).join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCSV(csvContent, `shared-leads-${new Date().toISOString().slice(0, 10)}.csv`);
    toast({ title: `Exported ${selectedLeadsForMerge.length} leads to CSV` });
  };

  const handleAssignToUser = async (targetUserId: string, targetUserName: string) => {
    if (selectedLeadIds.size === 0 || !userId) return;

    const leadIdsArray = Array.from(selectedLeadIds);
    const rows = leadIdsArray.map((leadId) => ({
      lead_id: leadId,
      user_id: targetUserId,
      assigned_by: userId,
    }));

    const { error } = await supabase
      .from('lead_assignments')
      .upsert(rows, { onConflict: 'lead_id,user_id' });

    if (error) {
      toast({ title: 'Failed to assign leads', variant: 'destructive' });
      return;
    }

    const currentUserName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.email || 'Unknown';
    try {
      await logActivitiesBatch(
        leadIdsArray.map((leadId) => ({
          leadId,
          type: 'other' as const,
          title: `Lead assigned to ${targetUserName}`,
          description: `${currentUserName} assigned this lead to ${targetUserName}`,
          metadata: { action: 'assigned', targetUserId, performedBy: userId },
          performedBy: userId,
        }))
      );
    } catch {
      // Activity logging is non-critical
    }

    toast({ title: `Assigned ${leadIdsArray.length} lead${leadIdsArray.length !== 1 ? 's' : ''} to ${targetUserName}` });
    setSelectedLeadIds(new Set());
    queryClient.invalidateQueries({ queryKey: ['shared-with-me'] });
  };

  const handleRemoveFromShare = async (lead: any) => {
    const shareId = lead._shareId;
    const share = shares?.find((s: any) => s.id === shareId);
    if (!share) return;

    const newIds = (share.lead_ids as string[]).filter((id: string) => id !== lead.id);

    if (newIds.length === 0) {
      // Delete the share entirely if no leads remain
      const { error } = await supabase.from('lead_shares').delete().eq('id', shareId);
      if (error) {
        toast({ title: 'Failed to remove lead', variant: 'destructive' });
        return;
      }
    } else {
      const { error } = await supabase.from('lead_shares').update({ lead_ids: newIds }).eq('id', shareId);
      if (error) {
        toast({ title: 'Failed to remove lead', variant: 'destructive' });
        return;
      }
    }

    toast({ title: `${lead.name} removed from shared list` });
    queryClient.invalidateQueries({ queryKey: ['shared-with-me'] });
  };

  const canRemoveLead = (lead: any) => lead._createdBy === userId;

  const activeShare = filterShareId ? shares?.find((s: any) => s.id === filterShareId) : null;
  const canRenameShare = activeShare && activeShare.created_by === userId;

  const handleStartRename = () => {
    setRenameValue(activeShare?.name || '');
    setIsRenaming(true);
    setTimeout(() => renameInputRef.current?.focus(), 0);
  };

  const handleSaveRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || !filterShareId) {
      setIsRenaming(false);
      return;
    }
    const { error } = await supabase.from('lead_shares').update({ name: trimmed }).eq('id', filterShareId);
    if (error) {
      toast({ title: 'Failed to rename', variant: 'destructive' });
    } else {
      toast({ title: 'Share renamed' });
      queryClient.invalidateQueries({ queryKey: ['shared-with-me'] });
      queryClient.invalidateQueries({ queryKey: ['share-lists-nav'] });
    }
    setIsRenaming(false);
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
  };

  const handleDeleteShare = async () => {
    if (!filterShareId) return;
    const { error } = await supabase.from('lead_shares').delete().eq('id', filterShareId);
    if (error) {
      toast({ title: 'Failed to delete share', variant: 'destructive' });
      return;
    }
    toast({ title: 'Share deleted' });
    queryClient.invalidateQueries({ queryKey: ['shared-with-me'] });
    queryClient.invalidateQueries({ queryKey: ['share-lists-nav'] });
    navigate('/shared-with-me');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!shares || shares.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Share2 className="h-12 w-12 text-muted-foreground" />
        <h2 className="text-xl font-semibold text-foreground">No shared leads yet</h2>
        <p className="text-sm text-muted-foreground">
          When someone in your company shares leads, they'll appear here.
        </p>
      </div>
    );
  }

  const sharedByColumn = {
    header: 'Shared by',
    render: (lead: any) => (
      <span className="text-muted-foreground text-sm">{lead._sharedBy}</span>
    ),
  };

  const shareNameColumn = {
    header: 'Share',
    render: (lead: any) => (
      <Badge variant="outline" className="text-xs font-normal">{lead._shareName}</Badge>
    ),
  };

  return (
    <div className="mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2">
          {filterShareId && isRenaming ? (
            <div className="flex items-center gap-2">
              <Input
                ref={renameInputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveRename();
                  if (e.key === 'Escape') handleCancelRename();
                }}
                onBlur={handleSaveRename}
                className="text-2xl font-bold h-10 w-80"
              />
              <Button variant="ghost" size="icon" className="h-8 w-8" onMouseDown={(e) => { e.preventDefault(); handleSaveRename(); }}>
                <Check className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onMouseDown={(e) => { e.preventDefault(); handleCancelRename(); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-foreground">
                {filterShareId
                  ? activeShare?.name || 'Shared Leads'
                  : 'All Shared Leads'}
              </h1>
              {canRenameShare && (
                <>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleStartRename}>
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <DeleteConfirmationDialog
                    entityName={activeShare?.name || 'this share'}
                    onConfirm={handleDeleteShare}
                    trigger={
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                      </Button>
                    }
                  />
                </>
              )}
            </>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          {filterShareId
            ? 'Leads from this shared list. Click any lead to view full details.'
            : 'Leads shared within your company. Click any lead to view full details.'}
        </p>
      </div>

      <SharedLeadsToolbar
        search={filters.search}
        onSearchChange={filters.setSearch}
        sortDir={filters.sortDir}
        onSortToggle={() => filters.setSortDir(filters.sortDir === 'asc' ? 'desc' : 'asc')}
        selectedCategories={filters.selectedCategories}
        onCategoriesChange={filters.setSelectedCategories}
        availableCategories={filters.availableCategories}
        selectedShareGroups={filters.selectedShareGroups}
        onShareGroupsChange={filters.setSelectedShareGroups}
        availableShareGroups={filters.availableShareGroups}
        view={filters.view}
        onViewChange={filters.setView}
        resultCount={filters.filteredLeads.length}
        selectedCount={selectedLeadIds.size}
        onMailMerge={handleMailMergeOpen}
        onExportCSV={handleExportCSV}
        onAssignToUser={handleAssignToUser}
        companyMembers={companyMembers}
        currentUserId={userId}
      />

      {filters.view === 'table' ? (
        <SharedLeadsTable
          leads={filters.filteredLeads}
          onRowClick={(id) => navigate(`/leads/${id}`)}
          clickable
          extraColumns={[shareNameColumn, sharedByColumn]}
          selectedLeadIds={selectedLeadIds}
          onSelectionChange={setSelectedLeadIds}
          keyContactsMap={keyContactsMap}
          onRemoveLead={handleRemoveFromShare}
          canRemove={canRemoveLead}
        />
      ) : (
        <div className="space-y-6">
          {groupedLeads.map((group) => (
            <div key={group.shareName + group.sharedAt}>
              <div className="flex items-baseline gap-2 mb-3">
                <h3 className="text-sm font-semibold text-foreground">{group.shareName}</h3>
                <span className="text-xs text-muted-foreground">
                  by {group.sharedBy} · {format(new Date(group.sharedAt), 'MMM d, yyyy')}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {group.leads.map((lead: any) => (
                  <Link
                    key={`${lead._shareId}-${lead.id}`}
                    to={`/leads/${lead.id}`}
                    className="block rounded-lg border border-border/60 p-3 hover:bg-accent/50 hover:border-primary/30 transition-all group"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-sm text-foreground line-clamp-2">
                        {lead.name}
                      </h3>
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" />
                    </div>
                    {lead.category && (
                      <Badge variant="outline" className="text-xs mt-1.5">{lead.category}</Badge>
                    )}
                    <div className="space-y-1 mt-2 text-xs text-muted-foreground">
                      {lead.address && (
                        <div className="flex items-start gap-1.5">
                          <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                          <span className="line-clamp-1">{lead.address}</span>
                        </div>
                      )}
                      {lead.rating && (
                        <div className="flex items-center gap-1.5">
                          <Star className="h-3 w-3 shrink-0 fill-current text-amber-500" />
                          <span>{lead.rating}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <MailMergeDialog
        open={mailMergeOpen}
        onOpenChange={handleMailMergeClose}
        leads={selectedLeadsForMerge}
      />
    </div>
  );
};

export default SharedWithMe;
